import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi } from "@/services/adminApi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Settings,
  Plug,
  Building2,
  RefreshCw,
  Trash,
  Brain,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import type {
  Organization,
  Connector,
  ConnectorsResponse,
} from "../types/admin";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table";
import OrganizationConfigPage from "./OrganizationConfigPage";
import { TranscriptionProviderCard } from "@/components/organizations/TranscriptionProviderCard";
import { toast } from "sonner";

interface EditOrgForm {
  name: string;
  emailDomain: string;
  verified: boolean;
  allowModelChange: boolean;
  enableToolDebugView: boolean;
}

export default function OrganizationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"info" | "env" | "connectors" | "agent-config">(
    "info"
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editForm, setEditForm] = useState<EditOrgForm>({
    name: "",
    emailDomain: "",
    verified: false,
    allowModelChange: false,
    enableToolDebugView: false,
  });

  // Connectors state
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [syncingConnectorId, setSyncingConnectorId] = useState<string | null>(
    null
  );
  const [deletingConnectorId, setDeletingConnectorId] = useState<string | null>(
    null
  );
  const [wsConnected, setWsConnected] = useState(false);

  useEffect(() => {
    if (id) {
      fetchOrganizationDetails(id);
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === "connectors") {
      fetchConnectors(id);
    }
  }, [id, activeTab]);

  // WebSocket
  useEffect(() => {
    if (!id || activeTab !== "connectors") return;

    const backendUrl =
      import.meta.env.VITE_BACKEND_API_URL || "http://localhost:3000";
    const url = new URL(backendUrl);
    const protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${url.host}/ws/admin/connector-progress?organizationId=${id}`;

    let ws: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log("WebSocket connected");
          setWsConnected(true);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            setConnectors((prev) =>
              prev.map((connector) => {
                if (connector.id === data.connectorId) {
                  return {
                    ...connector,
                    syncedData: data.syncedData,
                    totalData: data.totalData,
                    status: data.status || connector.status,
                  };
                }
                return connector;
              })
            );
          } catch (error) {
            console.error("Failed to parse WebSocket message:", error);
          }
        };

        ws.onerror = (error) => {
          console.error("WebSocket error:", error);
          setWsConnected(false);
        };

        ws.onclose = () => {
          console.log("WebSocket closed, reconnecting in 3s...");
          setWsConnected(false);

          reconnectTimeout = setTimeout(() => {
            connect();
          }, 3000);
        };
      } catch (error) {
        console.error("Failed to create WebSocket connection:", error);
      }
    };

    connect();

    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [id, activeTab]);

  const fetchOrganizationDetails = async (orgId: string) => {
    try {
      setIsLoading(true);
      const orgDetails = await adminApi.getOrganization(orgId);
      setOrganization(orgDetails);
      setEditForm({
        name: orgDetails.name,
        emailDomain: orgDetails.emailDomain,
        verified: orgDetails.verified,
        allowModelChange: orgDetails.allowModelChange,
        enableToolDebugView: orgDetails.enableToolDebugView,
      });
    } catch (error) {
      console.error("Failed to fetch organization details:", error);
      toast.error("Failed to fetch organization details");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConnectors = async (orgId: string) => {
    try {
      setLoadingConnectors(true);
      const response: ConnectorsResponse = await adminApi.getConnectors(orgId);
      setConnectors(response.connectors);
    } catch (error) {
      console.error("Failed to fetch connectors:", error);
      toast.error("Failed to fetch connectors");
    } finally {
      setLoadingConnectors(false);
    }
  };

  const handleEditOrganization = async () => {
    if (!editForm.name || !editForm.emailDomain || !organization) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      setIsSubmitting(true);
      await adminApi.updateOrganization(organization.id, editForm);
      toast.success("Organization updated successfully!");
      setIsEditing(false);
      await fetchOrganizationDetails(organization.id);
    } catch (error) {
      console.error("Failed to update organization:", error);
      toast.error("Failed to update organization");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditFormChange = (
    field: keyof EditOrgForm,
    value: string | boolean
  ) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSyncConnector = async (connectorId: string) => {
    try {
      setSyncingConnectorId(connectorId);
      await adminApi.syncConnector(connectorId);
      await fetchConnectors(id!);
      toast.success("Connector sync initiated successfully!");
    } catch (error) {
      console.error("Failed to sync connector:", error);
      toast.error("Failed to sync connector. Please try again.");
    } finally {
      setSyncingConnectorId(null);
    }
  };

  const handleDeleteConnector = async (connectorId: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this connector? This action cannot be undone."
    );
    if (!confirmed) return;

    try {
      setDeletingConnectorId(connectorId);
      await adminApi.deleteConnector(connectorId);
      await fetchConnectors(id!);
      toast.success("Connector deleted successfully!");
    } catch (error) {
      console.error("Failed to delete connector:", error);
      toast.error("Failed to delete connector. Please try again.");
    } finally {
      setDeletingConnectorId(null);
    }
  };

  const [agentConfigSaving, setAgentConfigSaving] = useState<string | null>(null);

  const handleAgentConfigToggle = async (
    field: "enableInstructions" | "enableToolOutputSummarization" | "AIResponseMemory",
    value: boolean
  ) => {
    if (!organization) return;
    setAgentConfigSaving(field);
    try {
      const updated = await adminApi.updateOrganization(organization.id, {
        [field]: value,
      } as Partial<typeof organization>);
      setOrganization(updated);
      toast.success("Agent configuration updated");
    } catch (error) {
      console.error("Failed to update agent config:", error);
      toast.error("Failed to update agent configuration");
    } finally {
      setAgentConfigSaving(null);
    }
  };

  const getStatusBadge = (active: boolean) => {
    return active ? (
      <Badge variant="success">Verified</Badge>
    ) : (
      <Badge variant="warning">Unverified</Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatConnectorType = (type: string) => {
    return type
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const getConnectorStatusColor = (status: string) => {
    switch (status) {
      case "syncCompleted":
        return "success";
      case "syncStarted":
        return "warning";
      case "syncFailed":
        return "error";
      default:
        return "default";
    }
  };

  const renderSyncProgress = (connector: Connector) => {
    const isDb = connector.type === "postgres" || connector.type === "mysql";
    const isAnalyzing = isDb && connector.status === "syncStarted";
    const hasFailed = connector.status === "syncFailed";

    if (isAnalyzing) {
      return (
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-xs text-gray-600">Analyzing database...</span>
        </div>
      );
    }

    if (hasFailed) {
      return <span className="text-xs text-red-600">Sync failed</span>;
    }

    if (
      connector.totalData !== undefined &&
      connector.syncedData !== undefined &&
      connector.totalData > 0
    ) {
      const percentage = Math.min(
        100,
        Math.round(
          (connector.syncedData / Math.max(1, connector.totalData)) * 100
        )
      );

      return (
        <div className="w-48">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600">
              {connector.syncedData} of {connector.totalData} items synced
            </span>
            <span className="text-xs font-medium text-gray-700">
              {percentage}%
            </span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-in-out"
              style={{
                width: `${percentage}%`,
              }}
            ></div>
          </div>
        </div>
      );
    }

    return (
      <span className="text-xs text-gray-500">No sync data available</span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/organizations")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Organizations
          </Button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">Organization not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/organizations")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              {organization.name}
            </h1>
            <p className="text-gray-600 mt-1">@{organization.emailDomain}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(organization.verified)}
          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              className="flex items-center"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({
                    name: organization.name,
                    emailDomain: organization.emailDomain,
                    verified: organization.verified,
                    allowModelChange: organization.allowModelChange,
                    enableToolDebugView: organization.enableToolDebugView,
                  });
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleEditOrganization}
                disabled={isSubmitting}
                className="flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("info")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === "info"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Building2 className="h-4 w-4 mr-2" />
            Organization Info
          </button>
          <button
            onClick={() => setActiveTab("env")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === "env"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Settings className="h-4 w-4 mr-2" />
            Env Config
          </button>
          <button
            onClick={() => setActiveTab("connectors")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === "connectors"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Plug className="h-4 w-4 mr-2" />
            Connectors ({organization._count?.connectors || 0})
          </button>
          <button
            onClick={() => setActiveTab("agent-config")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === "agent-config"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Brain className="h-4 w-4 mr-2" />
            Agent Configuration
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "info" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Organization Details */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Organization Information
              </h3>
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-left font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <Input
                      value={editForm.name}
                      onChange={(e) =>
                        handleEditFormChange("name", e.target.value)
                      }
                      placeholder="Organization name"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-left font-medium text-gray-700 mb-1">
                      Email Domain *
                    </label>
                    <Input
                      value={editForm.emailDomain}
                      onChange={(e) =>
                        handleEditFormChange("emailDomain", e.target.value)
                      }
                      placeholder="example.com"
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      id="verified"
                      type="checkbox"
                      checked={editForm.verified}
                      onChange={(e) =>
                        handleEditFormChange("verified", e.target.checked)
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="verified"
                      className="text-sm font-medium text-gray-700"
                    >
                      Verified
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      id="allowModelChange"
                      type="checkbox"
                      checked={editForm.allowModelChange}
                      onChange={(e) =>
                        handleEditFormChange(
                          "allowModelChange",
                          e.target.checked
                        )
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="allowModelChange"
                      className="text-sm font-medium text-gray-700"
                    >
                      Allow Model Change
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      id="enableToolDebugView"
                      type="checkbox"
                      checked={editForm.enableToolDebugView}
                      onChange={(e) =>
                        handleEditFormChange(
                          "enableToolDebugView",
                          e.target.checked
                        )
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="enableToolDebugView"
                      className="text-sm font-medium text-gray-700"
                    >
                      Enable Tool Debug View
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">
                      Name
                    </span>
                    <span className="text-sm text-gray-900 font-semibold">
                      {organization.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">
                      Email Domain
                    </span>
                    <span className="text-sm text-gray-900">
                      @{organization.emailDomain}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">
                      Verified
                    </span>
                    <Badge
                      variant={organization.verified ? "success" : "warning"}
                    >
                      {organization.verified ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">
                      Allow Model Change
                    </span>
                    <Badge
                      variant={
                        organization.allowModelChange ? "success" : "default"
                      }
                    >
                      {organization.allowModelChange ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">
                      Enable Tool Debug View
                    </span>
                    <Badge
                      variant={
                        organization.enableToolDebugView ? "success" : "default"
                      }
                    >
                      {organization.enableToolDebugView ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">
                      Created
                    </span>
                    <span className="text-sm text-gray-900">
                      {formatDate(organization.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-sm font-medium text-gray-500">
                      Last Updated
                    </span>
                    <span className="text-sm text-gray-900">
                      {formatDate(organization.updatedAt)}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Statistics
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-500">
                    Total Users
                  </span>
                  <span className="text-2xl font-bold text-gray-900">
                    {organization._count?.users || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-500">
                    Total Connectors
                  </span>
                  <span className="text-2xl font-bold text-gray-900">
                    {organization._count?.connectors || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <TranscriptionProviderCard
            organization={organization}
            onSaved={() => fetchOrganizationDetails(organization.id)}
          />
        </div>
      )}

      {activeTab === "env" && id && (
        <OrganizationConfigPage organizationId={id} />
      )}

      {activeTab === "connectors" && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Connectors ({connectors.length})
              </h3>
            </div>

            {loadingConnectors ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : connectors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Plug className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No connectors found for this organization</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Last Synced</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {connectors.map((connector) => (
                    <TableRow key={connector.id}>
                      <TableCell>
                        <div className="font-medium text-gray-900">
                          {connector.name && connector.name.length > 30 ? (
                            <span title={connector.name}>
                              {connector.name.substring(0, 30)}&hellip;
                            </span>
                          ) : (
                            connector.name
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-gray-900">
                          {formatConnectorType(connector.type)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          {renderSyncProgress(connector)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {connector.userName || "Unknown"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {connector.userEmail || "—"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {connector.lastSynced
                            ? formatDate(connector.lastSynced)
                            : "Never"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncConnector(connector.id)}
                            disabled={
                              syncingConnectorId === connector.id ||
                              deletingConnectorId === connector.id
                            }
                            className="h-8 px-2 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                            title="Sync connector"
                          >
                            <RefreshCw
                              className={`h-3 w-3 mr-1  ${
                                syncingConnectorId === connector.id
                                  ? "animate-spin"
                                  : ""
                              }`}
                            />
                            <span className="text-xs text-gray-600">Sync</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteConnector(connector.id)}
                            disabled={
                              deletingConnectorId === connector.id ||
                              syncingConnectorId === connector.id
                            }
                            className="h-8 px-2 hover:bg-red-50 hover:border-red-300 transition-colors"
                            title="Delete connector"
                          >
                            <Trash className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "agent-config" && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Agent Configuration
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Configure how AI agents behave for this organization.
            </p>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-4 border-b border-gray-100">
                <div className="space-y-1 flex-1 pr-4">
                  <label className="text-sm font-medium text-gray-900">
                    Enable Instructions
                  </label>
                  <p className="text-xs text-gray-500">
                    Allow agents to use custom instructions and system prompts to guide their behavior and responses.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {agentConfigSaving === "enableInstructions" && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  )}
                  <input
                    type="checkbox"
                    checked={organization?.enableInstructions ?? false}
                    onChange={(e) => handleAgentConfigToggle("enableInstructions", e.target.checked)}
                    disabled={agentConfigSaving === "enableInstructions"}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between py-4 border-b border-gray-100">
                <div className="space-y-1 flex-1 pr-4">
                  <label className="text-sm font-medium text-gray-900">
                    Process AI Responses for Memory
                  </label>
                  <p className="text-xs text-gray-500">
                    When enabled, memory extraction will process both user messages and AI assistant responses. When disabled, only user messages are processed.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {agentConfigSaving === "AIResponseMemory" && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  )}
                  <input
                    type="checkbox"
                    checked={organization?.AIResponseMemory ?? false}
                    onChange={(e) => handleAgentConfigToggle("AIResponseMemory", e.target.checked)}
                    disabled={agentConfigSaving === "AIResponseMemory"}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between py-4">
                <div className="space-y-1 flex-1 pr-4">
                  <label className="text-sm font-medium text-gray-900">
                    Enable Tool Output Summarization
                  </label>
                  <p className="text-xs text-gray-500">
                    Automatically summarize large tool outputs using a smaller model to reduce token usage and improve response times.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {agentConfigSaving === "enableToolOutputSummarization" && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  )}
                  <input
                    type="checkbox"
                    checked={organization?.enableToolOutputSummarization ?? false}
                    onChange={(e) => handleAgentConfigToggle("enableToolOutputSummarization", e.target.checked)}
                    disabled={agentConfigSaving === "enableToolOutputSummarization"}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
